import React from 'react';

import Link from 'next/link';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Text from '@components/common/Text/Text';
import ObjectiveActivated from '@components/lpc/Pot/AmountModule/Objective/ObjectiveActivated/ObjectiveActivated';
import { personRoles } from '@models/PotModel/personRoles';
import { getRoute, ROUTE } from '@services/http/Route';
import { formatCurrency } from '@services/utils/StringService';

import css from './Amount.module.scss';

type AmountProps = {
    spentAmount: number;
    amountLeft: number;
    collectorAmountLeft: number;
    uniq: string;
    collectedAmount: number;
    contributionAmount?: number;
    isContribution?: boolean;
    isPotReceivedSection?: boolean;
    hideCollectedAmount?: boolean;
    isPotPage?: boolean;
    personRole?: string;
    amountToReach?: number;
    dateLimit?: string;
    createdOn?: string;
};

const Amount = ({
    spentAmount,
    amountLeft,
    collectorAmountLeft,
    uniq,
    collectedAmount,
    contributionAmount,
    isContribution,
    isPotReceivedSection,
    hideCollectedAmount,
    isPotPage,
    personRole,
    amountToReach,
    dateLimit,
    createdOn,
}: AmountProps) => {
    const { t } = useTranslateLpc('lpc-dashboard');
    return (
        <div className={!isPotPage ? css.amount__bordered : css.amount}>
            {!isContribution && !(hideCollectedAmount && personRole === personRoles.PARTICIPANT) && (
                <div className={`${css.amount__title} ${!isPotPage ? css.amount__title__dashboard : ''}`}>
                    {!isPotPage ? (
                        <Text variant={'caption_bold_medium'} color={'txt-primary'}>
                            {isPotReceivedSection
                                ? formatCurrency(collectorAmountLeft)
                                : formatCurrency(collectedAmount)}
                        </Text>
                    ) : (
                        <h3>
                            {isPotReceivedSection
                                ? formatCurrency(collectorAmountLeft)
                                : formatCurrency(collectedAmount)}
                        </h3>
                    )}

                    {isPotPage &&
                        (personRole === personRoles.BENEFICIARY ? (
                            <Text
                                variant={'caption_bold_small'}
                                color={'txt-primary'}
                                customClass={css.amount__title__right_txt}
                            >
                                {t(`pots.offered`)}
                            </Text>
                        ) : (
                            <>
                                <Text
                                    variant={'caption_bold_small'}
                                    color={'txt-primary'}
                                    customClass={css.amount__title__right_txt}
                                >
                                    {t(`pots.collected`)}
                                </Text>
                                {amountToReach > 0 && (
                                    <Text
                                        variant={'caption_bold_small'}
                                        color={'txt-primary'}
                                        customClass={css.amount__title__right_txt}
                                    >
                                        {t(`pots.objective-on`)} {formatCurrency(amountToReach)}
                                    </Text>
                                )}
                                <ObjectiveActivated
                                    collectedAmount={collectedAmount}
                                    amountToReach={amountToReach}
                                    isObjectiveCompleted={collectedAmount === amountToReach}
                                    createdOn={createdOn}
                                    dateLimit={dateLimit}
                                />
                            </>
                        ))}
                </div>
            )}

            {hideCollectedAmount && personRole === personRoles.PARTICIPANT && (
                <div className={css.amount__txt}>
                    <Text variant={'caption_semi_bold'} color={'txt-primary'}>
                        {t('pots.card.collected-amount')}
                    </Text>
                    <Text variant={'caption_semi_bold'} color={'txt-primary'}>
                        {t('pots.card.hidden')}
                    </Text>
                </div>
            )}

            {personRole && personRole !== personRoles.PARTICIPANT && (
                <>
                    <div className={css.amount__txt}>
                        <Text variant={'caption_semi_bold'} color={'txt-primary'}>
                            {isContribution ? t('pots.card.collected-amount') : t('pots.card.spent-amount')}
                        </Text>
                        {!hideCollectedAmount ? (
                            <>
                                <Text variant={'caption_semi_bold'} color={'brand-primary'}>
                                    {isContribution ? formatCurrency(collectedAmount) : formatCurrency(spentAmount)}
                                </Text>
                                {!isContribution && spentAmount > 0 && (
                                    <Link href={getRoute(ROUTE.LPC.DASHBOARD.MY_SPENDINGS, `?pot=`, uniq)}>
                                        <Text
                                            variant={'body_02'}
                                            customClass={css.amount__txt__link}
                                            color={'txt-primary'}
                                        >
                                            {t('pots.card.show-history')}
                                        </Text>
                                    </Link>
                                )}
                            </>
                        ) : (
                            <Text variant={'body_02'} color={'brand-primary'}>
                                {t('pots.card.hidden')}
                            </Text>
                        )}
                    </div>
                    <div className={css.amount__txt}>
                        <Text variant={'caption_semi_bold'} color={'txt-primary'}>
                            {isContribution ? t('pots.card.contribution-amount') : t('pots.card.amount-left')}
                        </Text>
                        <Text variant={'caption_semi_bold'} color={'brand-primary'}>
                            {isContribution ? formatCurrency(contributionAmount) : formatCurrency(amountLeft)}
                        </Text>
                        {!isContribution && amountLeft > 0 && personRole === personRoles.ORGANISER && (
                            <Link href={getRoute(ROUTE.LPC.POT.POT, uniq, ROUTE.LPC.POT.SPENDING)}>
                                <Text variant={'body_02'} customClass={css.amount__txt__link} color={'txt-primary'}>
                                    {t('pots.card.use-money')}
                                </Text>
                            </Link>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Amount;
